package org.example.project.login

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

enum class LoginTarget {
    DESKTOP,
    MOBILE
}

data class LoginFlowRequest(
    val kennung: String,
    val password: String,
    val target: LoginTarget,
    val loginRequestId: String,
    val zweiFaktorToken: String = "",
    val zweiFaktorCode: String = "",
    val trustedDevice: Boolean = false
)

sealed class LoginFlowResult {
    data class Authenticated(val user: All) : LoginFlowResult()
    data class ZweiFaktor(
        val token: String,
        val setup: Boolean,
        val qrCode: String
    ) : LoginFlowResult()
    data class Rejected(val message: String) : LoginFlowResult()
}

object UnifiedLoginFlow {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun submit(request: LoginFlowRequest): LoginFlowResult =
        if (request.zweiFaktorToken.isBlank()) {
            start(request)
        } else {
            verifyZweiFaktor(request)
        }

    private suspend fun start(request: LoginFlowRequest): LoginFlowResult {
        if (org.example.project.service.reloadForLatestLoginBuild()) {
            return LoginFlowResult.Rejected("Die aktuelle Version wird geladen.")
        }
        val user = runCatching {
            val response = window.fetch(
                "${LumaticaApiSettings.getBaseUrl()}/api/desktoploginscreen/login-json",
                RequestInit(
                    method = "POST",
                    headers = Headers().apply { append("Content-Type", "application/json") },
                    body = json.encodeToString(
                        buildJsonObject {
                            put("kennung", request.kennung.trim())
                            put("password", request.password)
                            put("loginRequestId", request.loginRequestId)
                            put("target", request.target.name.lowercase())
                        }
                    )
                )
            ).await()
            val raw = response.text().await()
            if (!response.ok) {
                val code = runCatching {
                    (json.parseToJsonElement(raw) as? JsonObject)?.get("error")?.jsonPrimitive?.content
                }.getOrNull().orEmpty()
                return LoginFlowResult.Rejected(loginErrorMessage(code, response.status.toInt()))
            }
            decodeUser(raw)
        }.getOrNull() ?: return LoginFlowResult.Rejected("Server nicht erreichbar. Bitte Verbindung prüfen.")

        accessError(user, request.target)?.let { return LoginFlowResult.Rejected(it) }

        val token = user.zweiFaktorToken
        if (user.zweiFaktorErforderlich) {
            if (token.isBlank()) return LoginFlowResult.Rejected("Zwei-Faktor-Anmeldung konnte nicht gestartet werden.")
            return LoginFlowResult.ZweiFaktor(
                token = token,
                setup = user.zweiFaktorEinrichtung,
                qrCode = user.zweiFaktorQrCode
            )
        }

        return authenticated(user)
    }

    private suspend fun verifyZweiFaktor(request: LoginFlowRequest): LoginFlowResult {
        if (request.zweiFaktorCode.length != 6) {
            return LoginFlowResult.Rejected("Bitte den sechsstelligen Code eingeben.")
        }
        val user = ZweiFaktorService.verifyLoginChallenge(
            request.zweiFaktorToken,
            request.zweiFaktorCode,
            request.trustedDevice
        ) ?: return LoginFlowResult.Rejected("Der Code ist falsch oder abgelaufen.")

        if (user.zweiFaktorFehler) {
            val message = if (user.zweiFaktorGesperrt) {
                "Der Benutzer wurde nach zu vielen falschen Codes gesperrt."
            } else {
                "Code falsch. Verbleibende Versuche: ${user.zweiFaktorVerbleibendeVersuche}"
            }
            return LoginFlowResult.Rejected(message)
        }

        accessError(user, request.target)?.let { return LoginFlowResult.Rejected(it) }
        return authenticated(user)
    }

    private fun authenticated(user: All): LoginFlowResult {
        return if (user.id.isNotBlank()) {
            LoginFlowResult.Authenticated(user)
        } else {
            LoginFlowResult.Rejected("Anmeldung wurde nicht abgeschlossen.")
        }
    }

    private fun accessError(user: All, target: LoginTarget): String? = when {
        user.gesperrt == true -> "Du wurdest vom Administrator gesperrt."
        target == LoginTarget.DESKTOP && !user.zugangDesktop ->
            "Desktop-Zugang ist für diesen Benutzer deaktiviert."
        target == LoginTarget.MOBILE && !user.zugangMobil ->
            "Mobiler Zugang ist für diesen Benutzer deaktiviert."
        else -> null
    }

    private fun decodeUser(raw: String): All? {
        if (raw.isBlank() || raw == "null") return null
        return runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }
            .getOrNull()
            ?.takeIf { it.id.isNotBlank() }
    }

    private fun loginErrorMessage(code: String, status: Int): String = when (code) {
        "login_identifier_missing" -> "Bitte E-Mail oder Personalnummer eingeben."
        "password_missing" -> "Bitte Passwort eingeben."
        "login_identifier_unknown" -> "E-Mail oder Personalnummer ist nicht bekannt."
        "password_invalid" -> "Passwort falsch."
        "user_blocked" -> "Der Benutzerzugang ist gesperrt."
        "desktop_access_denied" -> "Desktop-Zugang ist für diesen Benutzer deaktiviert."
        "mobile_access_denied" -> "Mobiler Zugang ist für diesen Benutzer deaktiviert."
        "wrong_company" -> "Der Benutzer gehört nicht zu diesem Unternehmen."
        "password_status_invalid" -> "Der Passwortstatus des Benutzers ist ungültig."
        "rate_limited" -> "Zu viele Anmeldeversuche. Bitte 60 Sekunden warten."
        else -> when (status) {
            429 -> "Zu viele Anmeldeversuche. Bitte 60 Sekunden warten."
            in 500..599 -> "Anmeldung ist derzeit technisch nicht möglich."
            else -> "Anmeldung konnte nicht abgeschlossen werden."
        }
    }
}

fun newLoginRequestId(): String =
    js("globalThis.crypto && globalThis.crypto.randomUUID ? globalThis.crypto.randomUUID() : String(Date.now()) + '-' + Math.random().toString(16).slice(2)") as String
